import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchCategories, createCategory, updateCategory, deleteCategory, createSubcategory, updateSubcategory, deleteSubcategory } from '../../services/categoryService';

// Async thunks
export const loadCategories = createAsyncThunk(
    'categories/loadCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchCategories();
            // Handle different possible response structures
            if (response && Array.isArray(response)) {
                return response;
            } else if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
                return response.data.data;
            } else if (response && response.data && Array.isArray(response.data)) {
                return response.data;
            } else if (response && response.categories && Array.isArray(response.categories)) {
                return response.categories;
            } else {
                console.warn('Unexpected response structure:', response);
                return [];
            }
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const addCategory = createAsyncThunk(
    'categories/addCategory',
    async (categoryData, { rejectWithValue }) => {
        try {
            const response = await createCategory(categoryData);
            // Handle different possible response structures
            if (response && response.data && response.data.data) {
                return response.data.data;
            } else if (response && response.data) {
                return response.data;
            } else if (response && response.category) {
                return response.category;
            } else {
                return response;
            }
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const editCategory = createAsyncThunk(
    'categories/editCategory',
    async ({ categoryId, categoryData }, { rejectWithValue }) => {
        try {
            const response = await updateCategory(categoryId, categoryData);
            // Handle different possible response structures
            let updatedData;
            if (response && response.data) {
                updatedData = response.data;
            } else if (response && response.category) {
                updatedData = response.category;
            } else {
                updatedData = response;
            }
            return { categoryId, categoryData: updatedData };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const removeCategory = createAsyncThunk(
    'categories/removeCategory',
    async (categoryId, { rejectWithValue }) => {
        try {
            await deleteCategory(categoryId);
            return categoryId;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const addSubcategory = createAsyncThunk(
    'categories/addSubcategory',
    async (subcategoryData, { rejectWithValue }) => {
        try {
            const response = await createSubcategory(subcategoryData);
            // Handle different possible response structures
            let newSubcategory;
            if (response && response.data && response.data.data) {
                newSubcategory = response.data.data;
            } else if (response && response.data) {
                newSubcategory = response.data;
            } else if (response && response.subcategory) {
                newSubcategory = response.subcategory;
            } else {
                newSubcategory = response;
            }
            return { categoryId: subcategoryData.category_id, subcategory: newSubcategory };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const editSubcategory = createAsyncThunk(
    'categories/editSubcategory',
    async ({ subcategoryId, subcategoryData }, { rejectWithValue }) => {
        try {
            const response = await updateSubcategory(subcategoryId, subcategoryData);
            // Handle different possible response structures
            let updatedData;
            if (response && response.data && response.data.data) {
                updatedData = response.data.data;
            } else if (response && response.data) {
                updatedData = response.data;
            } else if (response && response.subcategory) {
                updatedData = response.subcategory;
            } else {
                updatedData = response;
            }
            return { subcategoryId, subcategoryData: updatedData };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const removeSubcategory = createAsyncThunk(
    'categories/removeSubcategory',
    async (subcategoryId, { rejectWithValue }) => {
        try {
            await deleteSubcategory(subcategoryId);
            return subcategoryId;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    categories: [],
    loading: false,
    error: null,
    operationLoading: false, // For individual operations like add/edit/delete
    operationError: null,
};

const categoriesSlice = createSlice({
    name: 'categories',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.operationError = null;
        },
        clearOperationError: (state) => {
            state.operationError = null;
        },
        setOperationLoading: (state, action) => {
            state.operationLoading = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Load categories
        builder
            .addCase(loadCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loadCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload;
                state.error = null;
            })
            .addCase(loadCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Add category
        builder
            .addCase(addCategory.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(addCategory.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.categories.push(action.payload);
                state.operationError = null;
            })
            .addCase(addCategory.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            });

        // Edit category
        builder
            .addCase(editCategory.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(editCategory.fulfilled, (state, action) => {
                state.operationLoading = false;
                const { categoryId, categoryData } = action.payload;
                const index = state.categories.findIndex(cat => (cat._id || cat.id) === categoryId);
                if (index !== -1) {
                    state.categories[index] = { ...state.categories[index], ...categoryData };
                }
                state.operationError = null;
            })
            .addCase(editCategory.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            });

        // Delete category
        builder
            .addCase(removeCategory.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(removeCategory.fulfilled, (state, action) => {
                state.operationLoading = false;
                state.categories = state.categories.filter(cat => (cat._id || cat.id) !== action.payload);
                state.operationError = null;
            })
            .addCase(removeCategory.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            });

        // Add subcategory
        builder
            .addCase(addSubcategory.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(addSubcategory.fulfilled, (state, action) => {
                state.operationLoading = false;
                const { categoryId, subcategory } = action.payload;
                const categoryIndex = state.categories.findIndex(cat => (cat._id || cat.id) === categoryId);
                if (categoryIndex !== -1) {
                    if (!state.categories[categoryIndex].subcategories) {
                        state.categories[categoryIndex].subcategories = [];
                    }
                    state.categories[categoryIndex].subcategories.push(subcategory);
                }
                state.operationError = null;
            })
            .addCase(addSubcategory.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            });

        // Edit subcategory
        builder
            .addCase(editSubcategory.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(editSubcategory.fulfilled, (state, action) => {
                state.operationLoading = false;
                const { subcategoryId, subcategoryData } = action.payload;
                // Find the category that contains this subcategory
                for (let categoryIndex = 0; categoryIndex < state.categories.length; categoryIndex++) {
                    const category = state.categories[categoryIndex];
                    if (category.subcategories) {
                        const subcategoryIndex = category.subcategories.findIndex(sub => (sub._id || sub.id) === subcategoryId);
                        if (subcategoryIndex !== -1) {
                            state.categories[categoryIndex].subcategories[subcategoryIndex] = { ...state.categories[categoryIndex].subcategories[subcategoryIndex], ...subcategoryData };
                            break;
                        }
                    }
                }
                state.operationError = null;
            })
            .addCase(editSubcategory.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            });

        // Remove subcategory
        builder
            .addCase(removeSubcategory.pending, (state) => {
                state.operationLoading = true;
                state.operationError = null;
            })
            .addCase(removeSubcategory.fulfilled, (state, action) => {
                state.operationLoading = false;
                const subcategoryId = action.payload;
                // Find the category that contains this subcategory
                for (let categoryIndex = 0; categoryIndex < state.categories.length; categoryIndex++) {
                    const category = state.categories[categoryIndex];
                    if (category.subcategories) {
                        const subcategoryIndex = category.subcategories.findIndex(sub => (sub._id || sub.id) === subcategoryId);
                        if (subcategoryIndex !== -1) {
                            state.categories[categoryIndex].subcategories.splice(subcategoryIndex, 1);
                            break;
                        }
                    }
                }
                state.operationError = null;
            })
            .addCase(removeSubcategory.rejected, (state, action) => {
                state.operationLoading = false;
                state.operationError = action.payload;
            });
    },
});

export const { clearError, clearOperationError, setOperationLoading } = categoriesSlice.actions;

// Selectors
export const selectCategories = (state) => state.categories.categories;
export const selectCategoriesLoading = (state) => state.categories.loading;
export const selectCategoriesError = (state) => state.categories.error;
export const selectOperationLoading = (state) => state.categories.operationLoading;
export const selectOperationError = (state) => state.categories.operationError;

export default categoriesSlice.reducer; 